import { OrderEvents, orderEventEmitter } from "@made-people/grease-order";
import { useEffect } from "react";
import { useMmGtm } from "../mm-gtm-context";
import { CodeVoucher, Voucher } from "@made-people/grease-cart";
import {
  gtmProductCategory,
  gtmProductParentId,
  gtmReturnVariantSku,
} from "../mm-gtm-helpers";
import shajs from "sha.js";

export const MmGtmEventPurchase = () => {
  const { pusher, frontendContext } = useMmGtm();

  useEffect(() => {
    /**
     * Purchase event
     */
    const onOrderPlaced = (params: OrderEvents["orderPlaced"]) => {
      const order = params.order;

      if (window.localStorage) {
        const orderTrackName = `orderTrack_${order.orderNumber}`;
        const orderTrackDate = localStorage.getItem(orderTrackName);
        if (orderTrackDate) {
          console.warn(`Order ${order.orderNumber} purchase already tracked`);
          const storedDate = new Date(orderTrackDate);
          const now = new Date();

          const diffInMs = now.getTime() - storedDate.getTime();
          const diffInDays = diffInMs / (1000 * 60 * 60 * 24);
          if (diffInDays > 365) {
            localStorage.removeItem(orderTrackName);
          }

          return;
        }

        // Use localStorage to prevent double tracking
        localStorage.setItem(orderTrackName, new Date().toISOString());
      }

      let coupon = "";
      if (order.vouchers?.length) {
        const codeVouchers: CodeVoucher[] = order.vouchers
          .filter((voucher: Voucher) => voucher.type === "code")
          .map((voucher: Voucher) => voucher as CodeVoucher);

        coupon = codeVouchers
          .map((voucher: CodeVoucher) => `${voucher.name} (${voucher.code})`)
          .join(", ");
      }

      const REVENUE_RATIO = {
        DE: 0.72,
        CY: 0.76,
        NL: 0.78,
        AT: 0.79,
        SE: 0.81,
        GB: 0.82,
        DK: 0.83,
        US: 0.85,
        CH: 0.85,
        FI: 0.87,
        BE: 0.87,
        PT: 0.87,
        SK: 0.87,
        MT: 0.87,
        NO: 0.87,
        CA: 0.88,
        SI: 0.88,
        MC: 0.89,
        ES: 0.89,
      };

      let fbRrevenue = 0;
      for (const item of order.items) {
        const returnPercentages = (() => {
          try {
            const returnPercentage = item.product.base.attributes.find(
              (a) => a.attributeKey === "return_percentage_by_country_key",
            )?.value;

            if (returnPercentage) {
              const data = JSON.parse(returnPercentage as string);
              return typeof data === "object" && data !== null ? data : {};
            }

            return {};
          } catch {
            return {};
          }
        })();
        const returnPercentage =
          returnPercentages[order.shippingAddress.country] ||
          (1 -
            REVENUE_RATIO[
              order.shippingAddress.country as keyof typeof REVENUE_RATIO
            ]) *
            100 ||
          10;
        fbRrevenue +=
          item.price.priceEachWithoutTax.number *
          item.quantity *
          ((100 - returnPercentage) / 100);
      }
      fbRrevenue +=
        order.totals.shipping.number /
        (1 + (order.totals.tax?.number ?? 0) / 100);
      fbRrevenue = parseFloat(fbRrevenue.toFixed(2));
      const postalCode = order.shippingAddress.zipCode ?? "";

      pusher({
        event: "mm_purchase",
        mmec: {
          purchase: {
            actionField: {
              hashedEmail: shajs("sha256")
                .update(order.shippingAddress.email)
                .digest("hex"),
              hashedFirstname: shajs("sha256")
                .update(order.shippingAddress.firstName)
                .digest("hex"),
              hashedLastname: shajs("sha256")
                .update(order.shippingAddress.lastName)
                .digest("hex"),
              em: order.shippingAddress.email,
              fn: order.shippingAddress.firstName,
              ln: order.shippingAddress.lastName,
              ph: order.shippingAddress.phoneNumber,
              id: order.orderNumber, // Transaction ID. Required for purchases and refunds.
              affiliation: frontendContext.marketName,
              revenue: order.totals.grandTotal.number, // Total transaction value (incl. tax and shipping)
              totalPrice: order.totals.grandTotal.number,
              tax: order.totals.tax?.number,
              shipping: order.totals.shipping.number,
              revenueSubtotal:
                order.totals.grandTotal.number -
                (order.totals.tax?.number ?? 0) -
                order.totals.shipping.number,

              coupon,
              fbRrevenue,
              postalCode,
              country: order.shippingAddress.country,
              city: order.shippingAddress.city,
              hashedCountry: shajs("sha256")
                .update(order.shippingAddress.country)
                .digest("hex"),
              hashedCity: shajs("sha256")
                .update(order.shippingAddress.city ?? "")
                .digest("hex"),
              hashedZip: shajs("sha256")
                .update(order.shippingAddress.zipCode ?? "")
                .digest("hex"),
              hashedPhoneNumber: shajs("sha256")
                .update(order.shippingAddress.phoneNumber ?? "")
                .digest("hex"),
              customerId: order.customer?.id,
              paymentMethod: order.paymentMethod,
            },
            products: order.items.map((item) => {
              const multiplier = 1;
              const price = item.price.priceEachWithoutTax.number;
              return {
                id: item.id,
                parentId: gtmProductParentId(item.product.base),
                name: gtmProductParentId(item.product.base),
                sku: item.sku,
                price,
                variant: gtmReturnVariantSku(item.product),
                brand: item.product.base.content.brand,
                quantity: item.quantity,
                category: gtmProductCategory(item.product.base),
                productRevenue: price * multiplier,
              };
            }),
          },
        },
      });
    };

    orderEventEmitter.on("orderPlaced", onOrderPlaced);
    return () => orderEventEmitter.off("orderPlaced", onOrderPlaced);
  }, []);

  return null;
};
